/**
 * useProcessingPubSub
 *
 * Reusable hook that mirrors the PubSub live-processing pattern from Matters.tsx.
 * Connects to the `ai_processing` PubSub hub, joins groups for items that are
 * actively processing, routes incoming events to per-item live state, and
 * triggers a refetch on completion/failure.
 *
 * Usage:
 *   const { processingStates, handleGroupJoined } = useProcessingPubSub({
 *     entityType: 'template',        // group prefix: template_{correlationId}
 *     getGroupName: (item) => ...,    // how to build the group name for an item
 *     getItemUuid: (item) => ...,     // how to extract the UUID
 *     isProcessing: (item) => ...,    // whether the item is actively processing
 *     onRefetch: () => fetchList(),   // called on completion/failure
 *     enabled: true,                  // set false when e.g. creation form is open
 *   });
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';

const HUB = 'ai_processing';

export interface LiveProcessingState {
  step?: string;
  message?: string;
  percent?: number;
  status?: string;
}

export interface UseProcessingPubSubOptions {
  /** Entity type label used for console logs (e.g. 'template', 'datasource', 'table') */
  entityType: string;

  /**
   * Given a list item, return the PubSub group name to join.
   * Return null/undefined to skip joining for that item.
   * Examples:
   *  - templates:   (item) => `template_${item.correlationId}`
   *  - datasources: (item) => `datasource_${item.correlationId}`
   *  - tables:      (item) => item.uuid   (tables use UUID directly as group)
   */
  getGroupName?: (item: any) => string | null | undefined;

  /** Extract the item's UUID/id used as the key in processingStates */
  getItemUuid: (item: any) => string;

  /** Return true if the item is actively processing and should be tracked */
  isProcessing: (item: any) => boolean;

  /** Called (debounced) when a completion or failure event is received */
  onRefetch: () => void;

  /** Set to false to skip PubSub connection (e.g. when creation form is visible) */
  enabled?: boolean;
}

export function useProcessingPubSub({
  entityType,
  getGroupName,
  getItemUuid,
  isProcessing,
  onRefetch,
  enabled = true,
}: UseProcessingPubSubOptions) {
  const [processingStates, setProcessingStates] = useState<Record<string, LiveProcessingState>>({});

  const activeGroupsRef = useRef<Set<string>>(new Set());
  const pubsubConnectedRef = useRef(false);
  const refetchRef = useRef(onRefetch);
  const refetchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refetch callback in sync
  useEffect(() => {
    refetchRef.current = onRefetch;
  }, [onRefetch]);

  // ── Handle incoming PubSub messages ──
  const handlePubSubMessage = useCallback(
    (raw: any, _eventName?: string) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
      const stepName = data.step_name || data.stepName || data.step || null;
      const message = data.message || '';
      const progressPercent = data.progress_percent;

      // Try to resolve the target UUID from the event payload
      const targetUuid =
        data.template_uuid || data.templateUuid ||
        data.datasource_uuid || data.dataSourceUuid ||
        data.table_uuid || data.tableUuid ||
        data.matter_uuid || data.matterUuid ||
        data.review_uuid || data.reviewUuid ||
        data.playbook_uuid || data.playbookUuid ||
        data.entity_uuid || null;

      const correlationId = data.correlationId || data.correlation_id || data.CorrelationID || null;

      const isCompletion = status === 'completed' || subType === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || subType === 'failed' || !!data.error;

      // Skip system/technical messages
      const isTechnical = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !message;
      if (isTechnical) return;

      // Update live processing state
      if (targetUuid && !isCompletion && !isFailure) {
        setProcessingStates(prev => ({
          ...prev,
          [targetUuid]: {
            step: stepName || prev[targetUuid]?.step,
            message: message || prev[targetUuid]?.message,
            percent: progressPercent ?? prev[targetUuid]?.percent,
            status: status || prev[targetUuid]?.status,
          },
        }));
      }

      if (isCompletion || isFailure) {
        // Clear live state for this item
        if (targetUuid) {
          setProcessingStates(prev => {
            const next = { ...prev };
            delete next[targetUuid];
            return next;
          });
        }

        // Leave the group
        if (correlationId) {
          const multiHubService = getMultiHubSocketService();
          // Try common group naming patterns
          const possibleGroups = [
            `${entityType}_${correlationId}`,
            correlationId, // tables use raw UUID
          ];
          for (const group of possibleGroups) {
            if (activeGroupsRef.current.has(group)) {
              multiHubService.emit(HUB, 'leave', { group });
              activeGroupsRef.current.delete(group);
            }
          }
        }
        if (targetUuid && activeGroupsRef.current.has(targetUuid)) {
          const multiHubService = getMultiHubSocketService();
          multiHubService.emit(HUB, 'leave', { group: targetUuid });
          activeGroupsRef.current.delete(targetUuid);
        }

        // Debounced refetch
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          refetchRef.current();
        }, 1500);
      }
    },
    [entityType]
  );

  // ── Connect to PubSub on mount ──
  useEffect(() => {
    if (!enabled) return;

    let mounted = true;

    const connect = async () => {
      const multiHubService = getMultiHubSocketService();
      const connected = await multiHubService.connectToHub(HUB);
      if (connected && mounted) {
        pubsubConnectedRef.current = true;
        multiHubService.on(HUB, '*', handlePubSubMessage);
      }
    };

    connect();

    return () => {
      mounted = false;
      const multiHubService = getMultiHubSocketService();

      // Leave all tracked groups
      for (const group of activeGroupsRef.current) {
        multiHubService.emit(HUB, 'leave', { group });
      }
      activeGroupsRef.current.clear();

      multiHubService.off(HUB, '*', handlePubSubMessage);
      multiHubService.disconnectHub(HUB);
      pubsubConnectedRef.current = false;

      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [enabled, handlePubSubMessage]);

  // ── Join groups for actively processing items (called after each fetch) ──
  const joinProcessingGroups = useCallback(
    (items: any[]) => {
      if (!pubsubConnectedRef.current || !getGroupName) return;
      const multiHubService = getMultiHubSocketService();

      items.forEach((item) => {
        if (!isProcessing(item)) return;

        const groupName = getGroupName(item);
        if (!groupName) return;

        if (!activeGroupsRef.current.has(groupName)) {
          const uuid = getItemUuid(item);
          console.log(`[${entityType} PubSub] Joining group ${groupName} for ${uuid}`);
          multiHubService.emit(HUB, 'join', { group: groupName });
          activeGroupsRef.current.add(groupName);
        }
      });
    },
    [entityType, getGroupName, getItemUuid, isProcessing]
  );

  // ── Callback for creation forms to register a new group ──
  const handleGroupJoined = useCallback((groupName: string) => {
    activeGroupsRef.current.add(groupName);
  }, []);

  // ── Re-register listener when coming back from creation form ──
  const reattachListener = useCallback(() => {
    if (pubsubConnectedRef.current) {
      const multiHubService = getMultiHubSocketService();
      multiHubService.off(HUB, '*', handlePubSubMessage);
      multiHubService.on(HUB, '*', handlePubSubMessage);
    }
  }, [handlePubSubMessage]);

  return {
    processingStates,
    joinProcessingGroups,
    handleGroupJoined,
    reattachListener,
  };
}
